import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LOGO_URL =
  "https://customer-assets-agu9un31.emergentagent.net/job_sparkle-tidycare/artifacts/d39d81c2b8d010cd_IMG_9276.PNG";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/services", label: "Services", testid: "nav-link-services" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 border-b border-gold-champagne/25 bg-ivory/80 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-10">
          <Link to="/" data-testid="nav-logo" className="flex items-center" onClick={() => setOpen(false)}>
            <span className="inline-block rounded-lg bg-white px-2.5 py-1.5 shadow-sm">
              <img src={LOGO_URL} alt="TidyCare" className="h-7 w-auto md:h-9" />
            </span>
          </Link>

          <nav className="hidden items-center gap-10 md:flex" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-ink/70 hover:text-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-quote-btn"
              className="btn-gold rounded-full px-6 py-2.5 text-xs font-bold uppercase tracking-[0.2em]"
            >
              Get a Free Quote
            </Link>
          </nav>

          <button
            data-testid="nav-menu-toggle"
            className="text-ink md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-40 flex flex-col justify-center bg-obsidian px-10 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <div className="flex flex-col gap-8">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className={`font-serif text-4xl ${
                      location.pathname === l.to ? "text-gold-gradient" : "text-ivory"
                    }`}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.6, ease: EASE }}
              >
                <Link
                  to="/contact"
                  data-testid="nav-mobile-quote-btn"
                  onClick={() => setOpen(false)}
                  className="btn-gold mt-4 inline-block rounded-full px-8 py-4 text-xs font-bold uppercase tracking-[0.2em]"
                >
                  Get a Free Quote
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
